import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { NewReport, Report, ReportRepositoryService } from '@xmedical/core';
import { PatientStore } from './patient.store';
/**
 * PatientReportsStore — saved clinical reports for the current patient.
 *
 * The 90% flow: a draft is created via createReport()/duplicate(), the
 * draft editor (CreateReportComponent) loads it via load() and persists
 * incremental edits via update(); the final save sets `locked: true` and
 * we reload the list. There is no push event for the reports list, so
 * cross-client refresh happens via reload() at known points.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientReportsStore {
  private patientStore = inject(PatientStore);
  private repo = inject(ReportRepositoryService);

  private readonly _items = signal<Report[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }
      this.fetch(patient.id);
    });
  }

  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  /**
   * Create a new report draft from a NewReport DTO (template + patient ids).
   * Returns the new report id; caller registers via DraftsService for
   * editing.
   */
  createReport(report: NewReport): Observable<number> {
    return this.repo.createReport(report);
  }

  /**
   * Duplicate an existing report. Returns the new report id; caller
   * registers via DraftsService.
   */
  duplicate(reportId: number): Observable<number> {
    return this.repo.duplicateReport(reportId);
  }

  /**
   * Update a report (used by the draft editor for incremental autosave
   * and the explicit lock on commit). Reloads the list when locked so
   * the new entry appears.
   */
  update(report: Report): Observable<boolean> {
    return this.repo.updateReport(report).pipe(
      tap({
        next: success => {
          if (success && report.locked) this.reload();
        }
      })
    );
  }

  /**
   * Fetch a single report by id (one-off lookup for the draft editor;
   * does not affect the list signal).
   */
  load(reportId: number): Observable<Report> {
    return this.repo.getReport(reportId);
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.repo.loadPatientReports(patientId).subscribe({
      next: reports => {
        this._items.set(reports);
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load patient reports');
        
      }
    });
  }
}
